import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tag, Empty, Spin } from 'antd';

import {
  selectors,
  fetchAdminPlans,
  fetchAdminAssets,
  fetchPlanContent,
  createPlan,
  clonePlan,
  publishPlan,
  hidePlan,
  unhidePlan,
  deletePlan,
  patchNode,
  createEdge,
  DevPlanAdmin,
} from 'state';

import {
  buildPlanNodes,
  computeEdges,
  computeLayout,
  indexByCode,
} from '../../TraineeDashboard/DevelopmentPlan/helpers';
import { adaptContentToLayout } from './adaptContentToLayout';
import { computeReadiness } from './hooks';
import PlanRail from './components/PlanRail';
import EditableTreeCanvas from './components/EditableTreeCanvas';
import NodeInspector from './components/NodeInspector';
import TrackInspector from './components/TrackInspector';
import ClusterInspector from './components/ClusterInspector';
import PlanOverview from './components/PlanOverview';
import CreatePlanModal, { CreatePlanValues } from './components/CreatePlanModal';
import PublishPreflightModal from './components/PublishPreflightModal';
import './devplans.css';

type Sel = { kind: 'plan' | 'node' | 'track' | 'cluster'; id: number };

const DevPlansEditor: React.FC = () => {
  const dispatch = useDispatch();
  const plans = useSelector(selectors.devAdminPlans);
  const content = useSelector(selectors.devAdminContent);
  const assets = useSelector(selectors.devAdminAssets);
  const saving = useSelector(selectors.devAdminSaving);
  const publishProblems = useSelector(selectors.devAdminPublishProblems);

  const [activeCode, setActiveCode] = useState<string | undefined>();
  const [sel, setSel] = useState<Sel>({ kind: 'plan', id: 0 });
  const [createModal, setCreateModal] = useState<{
    open: boolean;
    mode: 'create' | 'clone';
    source?: DevPlanAdmin | null;
  }>({ open: false, mode: 'create' });
  const [publishOpen, setPublishOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAdminPlans({}));
    dispatch(fetchAdminAssets({}));
  }, [dispatch]);

  useEffect(() => {
    if (activeCode) {
      dispatch(fetchPlanContent({ code: activeCode }));
      setSel({ kind: 'plan', id: 0 });
    }
  }, [activeCode, dispatch]);

  // Auto-open a freshly created / cloned plan once it lands in the list.
  useEffect(() => {
    if (pendingOpen && plans.some((p) => p.code === pendingOpen)) {
      setActiveCode(pendingOpen);
      setPendingOpen(null);
      setCreateModal((m) => ({ ...m, open: false }));
    }
  }, [pendingOpen, plans]);

  // Close the publish modal once the plan flips to published.
  useEffect(() => {
    if (
      publishOpen &&
      content &&
      content.plan.code === activeCode &&
      content.plan.status === 'published'
    ) {
      setPublishOpen(false);
    }
  }, [publishOpen, content, activeCode]);

  const isActiveContent = !!content && content.plan.code === activeCode;
  const plan = isActiveContent ? content!.plan : plans.find((p) => p.code === activeCode);
  const editable = plan?.status === 'draft';

  const view = useMemo(
    () => (isActiveContent ? adaptContentToLayout(content!) : null),
    [isActiveContent, content]
  );
  const layout = useMemo(() => {
    if (!view) return null;
    const nodes = buildPlanNodes(view.state);
    return {
      nodes,
      byCode: indexByCode(nodes),
      positions: computeLayout(nodes, view.state.tracks),
      edges: computeEdges(nodes),
    };
  }, [view]);
  const readiness = useMemo(
    () => computeReadiness(isActiveContent ? content : undefined, assets),
    [isActiveContent, content, assets]
  );

  const trackIdByCode = useMemo(() => {
    const map: Record<string, number> = {};
    content?.tracks.forEach((t) => {
      map[t.code] = t.id;
    });
    return map;
  }, [content]);

  /* ---- canvas handlers (translate code ↔ id) ---------------------------- */
  const nodeIdByCode = view?.nodeIdByCode ?? {};
  const onSelectNode = (code: string) => {
    const id = nodeIdByCode[code];
    if (id != null) setSel({ kind: 'node', id });
  };
  const onMoveNodeToTrack = (code: string, trackCode: string) => {
    const id = nodeIdByCode[code];
    const trackId = trackIdByCode[trackCode];
    if (id != null && trackId != null && activeCode) {
      dispatch(patchNode({ id, plan: activeCode, track: trackId }));
    }
  };
  const onCreateEdge = (fromCode: string, toCode: string) => {
    const from = nodeIdByCode[fromCode];
    const to = nodeIdByCode[toCode];
    if (from == null || to == null || !activeCode || !content) return;
    const exists = content.edges.some(
      (e) => e.from_node === from && e.to_node === to && e.kind === 'prereq'
    );
    if (exists) return;
    dispatch(
      createEdge({ plan: activeCode, from_node: from, to_node: to, kind: 'prereq', required_rank: 1 })
    );
  };

  /* ---- lifecycle -------------------------------------------------------- */
  const openClone = (p: DevPlanAdmin) =>
    setCreateModal({ open: true, mode: 'clone', source: p });
  const onCreateSubmit = (v: CreatePlanValues) => {
    if (createModal.mode === 'clone' && createModal.source) {
      dispatch(
        clonePlan({ code: createModal.source.code, new_code: v.code, new_name: v.name })
      );
    } else {
      dispatch(createPlan({ code: v.code, name: v.name, description: v.description }));
    }
    setPendingOpen(v.code);
  };
  const onDeletePlan = (p: DevPlanAdmin) => {
    dispatch(deletePlan({ code: p.code }));
    if (p.code === activeCode) setActiveCode(undefined);
  };

  /* ---- inspector selection --------------------------------------------- */
  let inspector: React.ReactNode = null;
  if (isActiveContent && plan) {
    if (sel.kind === 'node') {
      const node = content!.nodes.find((n) => n.id === sel.id);
      inspector = node ? (
        <NodeInspector
          key={node.id}
          node={node}
          planCode={activeCode!}
          content={content!}
          assets={assets}
          editable={!!editable}
          onSelectNodeId={(id) => setSel(id < 0 ? { kind: 'plan', id: 0 } : { kind: 'node', id })}
        />
      ) : null;
    } else if (sel.kind === 'track') {
      const track = content!.tracks.find((t) => t.id === sel.id);
      inspector = track ? (
        <TrackInspector
          key={track.id}
          track={track}
          planCode={activeCode!}
          editable={!!editable}
          clusters={content!.clusters.filter((c) => c.track === track.id)}
          onSelectCluster={(id) => setSel({ kind: 'cluster', id })}
        />
      ) : null;
    } else if (sel.kind === 'cluster') {
      const cluster = content!.clusters.find((c) => c.id === sel.id);
      inspector = cluster ? (
        <ClusterInspector
          key={cluster.id}
          cluster={cluster}
          planCode={activeCode!}
          editable={!!editable}
        />
      ) : null;
    }
    if (!inspector) {
      inspector = <PlanOverview plan={plan} editable={!!editable} readiness={readiness} />;
    }
  }

  return (
    <div className="devadmin studio2">
      <div className="devadmin__topbar">
        <div className="devadmin__id">
          <span className="devadmin__title">{plan?.name ?? 'Development Plans'}</span>
          {plan && (
            <Tag color={editable ? 'gold' : 'green'} bordered={false}>
              {plan.status}
            </Tag>
          )}
          {plan?.hidden && <Tag bordered={false}>hidden</Tag>}
          {plan?.is_default && (
            <Tag color="blue" bordered={false}>
              default
            </Tag>
          )}
        </div>
        <div className="devadmin__actions">
          {plan && (
            <button
              type="button"
              className={`devadmin-chip ${readiness.ready ? 'ok' : 'warn'}`}
              onClick={() => setPublishOpen(true)}
            >
              {readiness.ready
                ? '● Ready'
                : `▲ ${readiness.problems.length} issue${
                    readiness.problems.length === 1 ? '' : 's'
                  }`}
            </button>
          )}
          <span className="devadmin__save">
            {saving > 0 ? 'Saving…' : isActiveContent ? 'All changes saved' : ''}
          </span>
          {plan && editable && (
            <>
              <Button type="primary" onClick={() => setPublishOpen(true)}>
                Publish
              </Button>
              <Button onClick={() => openClone(plan)}>Clone</Button>
            </>
          )}
          {plan && !editable && (
            <Button type="primary" onClick={() => openClone(plan)}>
              Clone to edit
            </Button>
          )}
          {plan &&
            (plan.hidden ? (
              <Button onClick={() => dispatch(unhidePlan({ code: plan.code }))}>Unhide</Button>
            ) : (
              <Button onClick={() => dispatch(hidePlan({ code: plan.code }))}>Hide</Button>
            ))}
        </div>
      </div>

      <div className="devadmin__body">
        <PlanRail
          plans={plans}
          activeCode={activeCode}
          onSelect={setActiveCode}
          onCreate={() => setCreateModal({ open: true, mode: 'create', source: null })}
          onClone={openClone}
          onHide={(p) => dispatch(hidePlan({ code: p.code }))}
          onUnhide={(p) => dispatch(unhidePlan({ code: p.code }))}
          onDelete={onDeletePlan}
        />

        <div className="devadmin__canvas-wrap">
          {isActiveContent && layout && view ? (
            <EditableTreeCanvas
              nodes={layout.nodes}
              byCode={layout.byCode}
              positions={layout.positions}
              edges={layout.edges}
              trackColor={view.trackColor}
              trackCodes={view.state.tracks.map((t) => t.code)}
              selectedCode={
                sel.kind === 'node'
                  ? view.codeByNodeId[sel.id] ?? null
                  : null
              }
              issueCodes={readiness.issueNodeCodes}
              inactiveCodes={view.inactiveCodes}
              editable={!!editable}
              onSelectNode={onSelectNode}
              onSelectTrack={(code) => {
                const id = trackIdByCode[code];
                if (id != null) setSel({ kind: 'track', id });
              }}
              onBackground={() => setSel({ kind: 'plan', id: 0 })}
              onMoveNodeToTrack={onMoveNodeToTrack}
              onCreateEdge={onCreateEdge}
            />
          ) : (
            <div className="devadmin__empty">
              {activeCode ? (
                <Spin tip="Loading tree…" />
              ) : (
                <Empty description="Select or create a tree to edit" />
              )}
            </div>
          )}
        </div>

        <div className="devadmin__inspector">{inspector}</div>
      </div>

      <CreatePlanModal
        open={createModal.open}
        mode={createModal.mode}
        source={createModal.source}
        onClose={() => setCreateModal((m) => ({ ...m, open: false }))}
        onSubmit={onCreateSubmit}
      />
      <PublishPreflightModal
        open={publishOpen}
        planName={plan?.name ?? ''}
        readiness={readiness}
        publishProblems={publishProblems}
        saving={saving > 0}
        onPublish={() => activeCode && dispatch(publishPlan({ code: activeCode }))}
        onClose={() => setPublishOpen(false)}
      />
    </div>
  );
};

/** Admin Development Plans editor tab. */
const DevPlans: React.FC = () => <DevPlansEditor />;

export default DevPlans;
